import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Search } from "lucide-react";
import { SiteLayout } from "@/layouts/site-layout";
import { SEO } from "@/components/SEO";

const Support = () => {
  const faqCategories = [
    {
      category: "Project Types",
      questions: [
        {
          q: "What kind of products can you build in 1 week?",
          a: "AI-powered SaaS, marketplaces, subscription apps, chatbots, internal tools, landing pages, and lead-gen tools."
        },
        {
          q: "Do you build mobile apps?",
          a: "We build responsive web apps that work beautifully on mobile. For native iOS/Android apps, we can scope a custom estimate."
        },
        {
          q: "Can you build AI products?",
          a: "Yes — we specialize in integrating AI APIs (GPT, Claude, etc.) into products. We don't train custom models from scratch."
        },
        {
          q: "What if my project is more complex?",
          a: "Request a Custom MVP Estimate and we'll scope a timeline that fits your needs."
        }
      ]
    },
    {
      category: "Payments & Refunds",
      questions: [
        {
          q: "How much does the One Week MVP cost?",
          a: "$1,999 for our standard package (Limited time: 50% off - now $999). Custom MVPs are quoted individually."
        },
        {
          q: "When do I pay?",
          a: "Payment is due after your Saturday kickoff call, before Sunday's scope freeze."
        },
        {
          q: "Can I get a refund?",
          a: "Yes — refunds are available only before Sunday's scope freeze. After requirements are locked, we begin work and refunds are no longer available."
        },
        {
          q: "Do you offer payment plans?",
          a: "Not currently. Full payment is required upfront."
        }
      ]
    },
    {
      category: "Process & Timelines",
      questions: [
        {
          q: "Why only 4 projects per week?",
          a: "Quality over quantity. We limit capacity to ensure every project gets the focus it deserves."
        },
        {
          q: "What happens after I book a seat?",
          a: "You'll join our Saturday kickoff call where we finalize scope, features, and deliverables."
        },
        {
          q: "Can I make changes midweek?",
          a: "No. Once scope freezes on Sunday, no changes are allowed. This protects the 7-day timeline."
        },
        {
          q: "What if I need changes after launch?",
          a: "Post-launch changes are covered in our 30-day support window or through monthly support packages."
        },
        {
          q: "How do you guarantee 7-day delivery?",
          a: "By freezing scope early, limiting projects per week, and using AI-accelerated workflows combined with human oversight."
        }
      ]
    },
    {
      category: "Technology & Tools",
      questions: [
        {
          q: "What tech stack do you use?",
          a: "Modern, scalable frameworks — typically Next.js, Node.js, Supabase, Stripe, and AI APIs. Stack choice depends on your product."
        },
        {
          q: "Will my product be scalable?",
          a: "Yes. We build on proven stacks that support growth and user scaling."
        },
        {
          q: "Do I own the code?",
          a: "100%. Full code ownership and handoff documentation are included."
        },
        {
          q: "What AI tools do you use?",
          a: "We integrate OpenAI, Anthropic Claude, and other AI APIs depending on your product needs."
        },
        {
          q: "Do you use no-code tools?",
          a: "Occasionally for prototyping, but most builds are custom code for flexibility and control."
        }
      ]
    },
    {
      category: "Add-Ons",
      questions: [
        {
          q: "What add-ons are available?",
          a: "Launch videos, Product Hunt launch kits, lead-gen micro-tools, and post-launch support packages."
        },
        {
          q: "Can I buy add-ons later?",
          a: "Yes — add-ons can be purchased anytime, even after your MVP is delivered."
        },
        {
          q: "How long do add-ons take?",
          a: "Launch videos and PH kits typically take 3–5 days. Micro-tools take 5–7 days."
        }
      ]
    },
    {
      category: "Support Policies",
      questions: [
        {
          q: "Do you provide ongoing support?",
          a: "Yes — 30 days of post-launch support is included. Monthly support packages are available after that."
        },
        {
          q: "How do I contact support?",
          a: "Email us at support@oneweekmvps.com or use the contact form below."
        },
        {
          q: "What's covered in post-launch support?",
          a: "Bug fixes, minor adjustments, deployment help, and documentation clarifications."
        },
        {
          q: "What if something breaks after launch?",
          a: "We handle critical bugs during the 30-day support window. After that, support is available through monthly packages."
        }
      ]
    }
  ];

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqCategories.flatMap(category =>
      category.questions.map(q => ({
        "@type": "Question",
        "name": q.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": q.a
        }
      }))
    )
  };

  return (
    <SiteLayout>
      <SEO
        title="MVP Development Support & FAQ | MVP Build FAQ & Support"
        description="MVP development support and FAQ. Learn what happens after MVP launch, support for MVP builders, post-launch support for startup founders and agencies. Get answers to MVP development process, timeline, and deliverables."
        keywords="MVP development support, MVP build FAQ, what happens after MVP launch, support for MVP builders, what kind of support do we provide after your one-week MVP is live, FAQ: MVP development process timeline deliverables for founders, post MVP launch support for startup founders and agencies"
        canonicalUrl={`${typeof window !== 'undefined' ? window.location.origin : 'https://oneweekmvps.com'}/support`}
        structuredData={faqStructuredData}
      />
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 md:pt-40 md:pb-32">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-heading leading-tight">
              Got <span className="text-primary">questions?</span>
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground">
              From process to pricing to tech stack — we've answered the questions founders ask most.
            </p>

            {/* Search Bar */}
            <div className="relative max-w-2xl mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                placeholder="Search FAQs..."
                className="pl-12 h-14 text-base"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Categories */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto space-y-24">
            {faqCategories.map((category, categoryIdx) => (
              <div key={categoryIdx} className="space-y-8">
                <h2 className="text-3xl md:text-4xl font-heading border-b border-border pb-4">
                  {category.category}
                </h2>

                <Accordion type="single" collapsible className="w-full">
                  {category.questions.map((item, itemIdx) => (
                    <AccordionItem key={itemIdx} value={`${categoryIdx}-${itemIdx}`}>
                      <AccordionTrigger className="text-left text-lg">
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground text-base">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-24 md:py-32 bg-card/30">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading">
              Still have <span className="text-primary">questions?</span> Let's talk.
            </h2>
            <p className="text-xl text-muted-foreground">
              Can't find what you're looking for? Reach out directly.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="text-base px-8">
                Contact Support
              </Button>
              <Button size="lg" variant="outline" className="text-base px-8" asChild>
                <a href="https://cal.com/buildwithrishi/15min">Book a Call</a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default Support;
